/**
 * Someone's saved signature, drawn on the claim where their name appears.
 *
 * A signature is meaningful only next to what it signs, so it is shown in the
 * same block as the role and the date rather than tucked away in a profile.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { api } from '@/api/client';
import { toDataUrl } from './imageBytes';

interface SignatureResponse {
  hasSignature: boolean;
  url?: string;
}

/**
 * Fetch a signature and prefer inlined bytes.
 *
 * Falls back to the presigned URL when the bytes cannot be read, so the mark is
 * still visible on screen even where the bucket has no CORS configuration.
 */
export async function loadSignatureSrc(
  fetchSignature: () => Promise<SignatureResponse>,
): Promise<string | null> {
  try {
    const sig = await fetchSignature();
    if (!sig.hasSignature || !sig.url) return null;
    return (await toDataUrl(sig.url)) ?? sig.url;
  } catch {
    // A missing signature must never stop a claim being read or submitted.
    return null;
  }
}

/** The current user's own signature, for the form they are filling in. */
export function useMySignature(): string | null {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    void loadSignatureSrc(() => api.signature.mine<SignatureResponse>()).then((value) => {
      if (!cancelled) setSrc(value);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return src;
}

export function SignatureMark({ src, name }: { src: string | null; name: string }): ReactNode {
  if (!src) return null;
  return <img className="sig-img" src={src} alt={`Signature of ${name}`} />;
}
